import axios from 'axios';
import once from 'once';
import {
    DeleteQcReportNoteParams,
    NoteType,
    UpdateQcJobStatusParams,
    EditQcReportNoteBody,
} from '@pluto/@types';
import {
    ContentPartnersFilterParams,
    CreateContentPartnerProps,
} from 'ContentPartners/ContentPartnersTypes';
import {
    SearchedContentRequest,
    ContentItemsSearchRequest,
} from 'ContentItem/Search/SearchTypes';
import {MediaOrdersItemSortParams} from 'MediaOrders/MediaOrdersTypes';
import {formQuery} from './form-query';
import {convertEmptyStringToNull} from './map-empty-string-to-null';
export * from './qc';
export * from './qc-types';
export * from '../Authorization';
export * from './users';
export {formQuery} from './form-query';

const getTextEnums = async () => {
    const {data} = await axios('/api/enums');

    return data;
};

const updateQcJobReportStatus = async (id: string, params: UpdateQcJobStatusParams) => {
    return await axios.put(`/api/qc-jobs/${id}/state`, params);
};

const deleteQcReportNote = async (params: DeleteQcReportNoteParams) => {
    const {qcJobId, timespanId} = params;

    return await axios.delete(`/api/qc-jobs/${qcJobId}/timespans/notes/${timespanId}`);
};

const editQcReportNote = async (
    params: DeleteQcReportNoteParams,
    body: EditQcReportNoteBody
) => {
    const {qcJobId, timespanId} = params;

    return await axios.put(`/api/qc-jobs/${qcJobId}/timespans/notes/${timespanId}`, body);
};

const getQcReportsNotes = async (id: string, subtype: NoteType) => {
    const query = formQuery(`/api/qc-jobs/${id}/timespans/notes`, {
        subtype,
    });
    const {data} = await axios(query);

    return data;
};

const deliveryJob = async (qcJobId: number) => {
    return await axios.post('/api/delivery-jobs/start', {
        qcJobId,
    });
};

const deliveryContentItem = async (contentItemId: number) => {
    return await axios.post('/api/delivery-jobs/start', {
        contentItemId,
    });
};

const getDeliveryManagerManifestJson = async (url: string) => {
    const {data} = await axios(url);

    return data;
};

const getDeliveryManagerManifest = async (id: string) => {
    const {data} = await axios(`/api/delivery-jobs/${id}/json-url`);

    return data;
};

const deliveryMetadataUpdates = async (qcJobId: number) => {
    return await axios.post('/api/delivery-jobs/start', {
        qcJobId,
    });
};

const getTargetAgeOptions = async (countryCode?: string) => {
    const query = formQuery('/api/target-age', {
        countryCode,
    });
    const {data} = await axios(query);

    return data;
};

const getJobDeliveryStatus = async (qcJobId: number) => {
    const {data} = await axios(`/api/qc-jobs/${qcJobId}/delivery/status`);
    return data;
};

const setQcReportNote = async (params: any, subtype: NoteType) => {
    const {id, body} = params;

    return await axios.post(`/api/qc-jobs/${id}/timespans/notes`, {
        ...body,
        subtype,
    });
};

const getQcReportsReasons = async (type: NoteType) => {
    const {data} = await axios(`/api/timespan-categories/${type}`);

    return data;
};

const getQcToolsArtwork = async (id: number) => {
    const {data} = await axios(`/api/qc-jobs/${id}/artwork`);

    return data;
};

const setQcToolsArtworkDeliverable = async (id: number) => {
    const {data} = await axios.patch(`/api/artwork/${id}/deliverable`);
    return data;
};

const setQcToolsArtworkRetry = async (id: number) => {
    const {data} = await axios.patch(`/api/artwork/${id}/retry`);
    return data;
};

const setQcToolsArtworkDelete = async (id: number) => {
    const {data} = await axios.delete(`/api/artwork/${id}`);
    return data;
};

const getContentItemViewArtwork = async (id: number) => {
    const {data} = await axios(`/api/content-items/${id}/artwork`);
    return data;
};

const getQcToolsMetadata = async (id: any) => {
    const {data} = await axios(`/api/qc-jobs/${id}/metadata`);

    return data;
};

const getContentItemViewMetadata = async (id: any) => {
    const {data} = await axios(`/api/content-items/${id}/metadata`);

    return data;
};

const getQcToolsMetadataGenres = async () => {
    const {data} = await axios(`/api/qc-jobs/genres`);

    return data;
};

const getContentItemViewMetadataGenres = async () => {
    const {data} = await axios(`/api/content-items/genres`);

    return data;
};

const getQcToolsTerritorialRatingDescriptors = async () => {
    const {data} = await axios('/api/qc-jobs/rating-descriptors');
    return data;
};

const getContentItemViewTerritorialRatingDescriptors = async () => {
    const {data} = await axios('/api/content-items/rating-descriptors');
    return data;
};

const updateQcToolsMetadataGeneral = async (id: number, params: any) =>
    await axios.patch(`/api/qc-jobs/${id}/metadata/general`, params);

const updateContentItemViewMetadataGeneral = async (id: number, params: any) =>
    await axios.patch(`/api/content-items/${id}/metadata/general`, params);

const getQcJobsStatuses = async () => {
    const {data} = await axios('/api/qc-jobs/statuses');
    return data;
};

const getDeliveryJobs = async (params: any) => {
    const query = formQuery('/api/delivery-jobs', params);
    const {data} = await axios(query);
    return data;
};

const cancelDeliveryJob = async (id: number) => {
    return await axios.post(`/api/delivery-jobs/${id}/cancel`);
};

const retryDeliveryJob = async (id: number) => {
    return await axios.post(`/api/delivery-jobs/${id}/restart`);
};

const getQcJobState = async (id: any) => {
    const {data} = await axios(`/api/qc-jobs/${id}/state`);
    return data;
};

const getQcReportsStatuses = async () => {
    const {data} = await axios('/api/qc-reports/statuses');
    return data;
};

const createNewIngestItem = async (params: any) => {
    const {body} = params;
    const newParams = convertEmptyStringToNull(body);

    return await axios.post('/api/ingest-configs', newParams);
};

const createContentPartner = async (params: CreateContentPartnerProps) => {
    const {body} = params;
    const newParams = convertEmptyStringToNull(body);

    return await axios.post('/api/content-partners', newParams);
};

const editIngestConfigItem = async (params: any) => {
    const {id, body} = params;
    const newParams = convertEmptyStringToNull(body);

    return await axios.put(`/api/ingest-configs/${id}`, newParams);
};

const editContentPartner = async (params: CreateContentPartnerProps) => {
    const {id, body} = params;
    const newParams = convertEmptyStringToNull(body);

    return await axios.put(`/api/content-partners/${id}`, newParams);
};

const deleteMediaOrder = async (params: any) => {
    const {mediaOrderId, mediaOrderItemId} = params;

    return await axios.delete(
        `/api/media-orders/${mediaOrderId}/media-order-items/${mediaOrderItemId}`
    );
};

const restoreMediaOrder = async (params: any) => {
    const {mediaOrderId, mediaOrderItemId} = params;

    return await axios.put(
        `/api/media-orders/${mediaOrderId}/media-order-items/${mediaOrderItemId}/restore`
    );
};

const getIngestConfigItemForEditing = async (id: any) => {
    const {data} = await axios(`/api/ingest-configs/${id}`);

    return data;
};

const getContentPartnerForEditing = async (id: number) => {
    const {data} = await axios(`/api/content-partners/${id}`);

    return data;
};

const getSearchedContentItems = async (params: SearchedContentRequest) => {
    const {search, lang, region, contType, contPartner, status, ...rest} = params;

    const mappedParams = {
        ...rest,
        language: lang,
        title: search,
        countryCode: region,
        contentItemType: contType,
        contentPartnerId: contPartner,
        workflowState: status,
    } as ContentItemsSearchRequest;

    const query = formQuery(`/api/content-items/search`, mappedParams);

    const {data} = await axios(query);
    return data;
};

const getContentPartnersList = async () => {
    const {data} = await axios('/api/content-partners');

    return data;
};

const getContentPartners = async (params: ContentPartnersFilterParams) => {
    const query = formQuery('/api/content-partners', params);
    const {data} = await axios(query);
    return data;
};

const getContentTypesList = async () => {
    const {data} = await axios('/api/ingest-sessions/types');

    return data;
};

const getImportMediaOrder = async (body: any) => {
    const data = await axios.post('/api/media-orders/import', body);

    return data;
};

const getImportQcJobsArtwork = async (id: number, body: FormData) => {
    return await axios.post(`/api/qc-jobs/${id}/artwork`, body);
};

const replaceImportQcJobsArtwork = async (id: number, artworkId: number, body: FormData) => {
    return await axios.put(`/api/qc-jobs/${id}/artwork/${artworkId}`, body);
};

const getImportContentItemArtwork = async (id: number, body: FormData) => {
    return await axios.post(`/api/content-items/${id}/artwork`, body);
};

const replaceImportContentItemArtwork = async (id: number,  artworkId: number, body: FormData) => {
    return await axios.put(`/api/content-items/${id}/artwork/${artworkId}`, body);
};

const getIngestStatusList = async () => {
    const {data} = await axios('/api/ingest-sessions/statuses');

    return data;
};

const getIngestSession = async (id: any) => {
    return await axios(`/api/ingest-sessions/${id}`);
};

const putIngestSession = async (id: string, data: {[key: string]: any}) => {
    const body = {
        data,
    };
    return await axios.put(`/api/ingest-sessions/${id}`, body);
};

const getContentItem = async (id: any) => {
    return await axios(`/api/content-items/${id}/full`);
};

const updateContentItem = async (id: any, body: any) => {
    return await axios.put(`/api/content-items/${id}/full`, body);
};

const getContentItemsList = async (params: any) => {
    const query = formQuery('/api/content-items', params);
    const {data} = await axios(query);

    return data;
};

const getIngestConfigs = async (params: any) => {
    const query = formQuery('/api/ingest-configs', params);
    const {data} = await axios(query);

    return data;
};

const getIngestList = async (params: {[key: string]: string}) => {
    const query = formQuery('/api/ingest-sessions', params);
    const {data} = await axios(query);

    return data;
};

const restartIngest = async (id: number) => {
    const query = `/api/ingest-sessions/${id}/restart`;
    const {data} = (await axios.post(query)) || {};

    return data;
};

const getIngestReport = async (id: any) => {
    const {data} = await axios(`/api/content-items/${id}/validation-reports/latest`);

    return data;
};

const getWorkflowHistory = async (id: any, params: any) => {
    const query = formQuery(`/api/content-items/${id}/workflow-history`, params);
    const {data} = await axios(query);

    return data;
};

const getMediaOrderItems = async (id: any, params: any) => {
    const query = formQuery(`/api/media-orders/${id}/media-order-items`, params);
    const {data} = await axios(query);

    return data;
};

const getMediaOrderSortedItems = async (
    id: string,
    params: MediaOrdersItemSortParams
) => {
    const query = formQuery(`/api/media-orders/${id}/media-order-items`, params);
    const {data} = await axios(query);

    return data;
};

const getMediaOrders = async (params: any) => {
    const query = formQuery('/api/media-orders', params);
    const {data} = await axios(query);

    return data;
};

const getMrssDefaults = once(async () => {
    const {data} = await axios('/api/ingest-configs/mrss-defaults');
    return data;
});

const getMetadataCategoriesList = async () => {
    const {data} = await axios('/api/qc-jobs/categories');

    return data;
};

const updateLocalizedMetadata = async (params: any) => {
    const {id, body} = params;

    return await axios.patch(`/api/qc-jobs/${id}/metadata/localized`, body);
};

const updateContentItemViewLocalizedMetadata = async (params: any) => {
    const {id, body} = params;

    return await axios.patch(`/api/content-items/${id}/metadata/localized`, body);
};

const updateTerritorialMetadata = async (params: any) => {
    const {id, body} = params;

    return await axios.patch(`/api/qc-jobs/${id}/metadata/territorial`, body);
};

const updateContentItemViewTerritorialMetadata = async (params: any) => {
    const {id, body} = params;

    return await axios.patch(`/api/content-items/${id}/metadata/territorial`, body);
};

const getContentItemViewTitles = async (id: any) => {
    const data = await axios(`/api/content-items/${id}/titles`);

    return data;
};

export {
    restoreMediaOrder,
    deleteMediaOrder,
    getIngestSession,
    getContentPartnersList,
    getContentTypesList,
    getContentItem,
    updateContentItem,
    getContentItemsList,
    getIngestConfigs,
    getIngestList,
    getIngestStatusList,
    getIngestReport,
    getMediaOrderItems,
    getMediaOrders,
    getSearchedContentItems,
    getImportMediaOrder,
    createNewIngestItem,
    editIngestConfigItem,
    getIngestConfigItemForEditing,
    getQcJobsStatuses,
    getQcReportsStatuses,
    getQcToolsArtwork,
    getContentItemViewArtwork,
    getMrssDefaults,
    getQcToolsMetadata,
    getContentItemViewMetadata,
    getQcJobState,
    getQcReportsReasons,
    getQcReportsNotes,
    setQcReportNote,
    deleteQcReportNote,
    updateQcJobReportStatus,
    editQcReportNote,
    getQcToolsMetadataGenres,
    getContentItemViewMetadataGenres,
    getQcToolsTerritorialRatingDescriptors,
    getContentItemViewTerritorialRatingDescriptors,
    updateTerritorialMetadata,
    updateQcToolsMetadataGeneral,
    getMetadataCategoriesList,
    updateLocalizedMetadata,
    getTextEnums,
    deliveryJob,
    getJobDeliveryStatus,
    getDeliveryJobs,
    retryDeliveryJob,
    cancelDeliveryJob,
    getContentPartners,
    createContentPartner,
    editContentPartner,
    getContentPartnerForEditing,
    getMediaOrderSortedItems,
    restartIngest,
    putIngestSession,
    getContentItemViewTitles,
    setQcToolsArtworkDeliverable,
    setQcToolsArtworkRetry,
    setQcToolsArtworkDelete,
    getWorkflowHistory,
    updateContentItemViewMetadataGeneral,
    updateContentItemViewLocalizedMetadata,
    updateContentItemViewTerritorialMetadata,
    getTargetAgeOptions,
    getDeliveryManagerManifest,
    getDeliveryManagerManifestJson,
    getImportQcJobsArtwork,
    getImportContentItemArtwork,
    deliveryMetadataUpdates,
    replaceImportQcJobsArtwork,
    replaceImportContentItemArtwork,
    deliveryContentItem,
};
